/*
 * Copyright (c) Fing Ltd. All rights reserved.
 */

import React, {Component} from 'react';
import {Link, withRouter} from "react-router-dom";

import {isUserSignedIn, UserContext} from "../UserContext";
import WaitingComponent from "../components/WaitingComponent";
import AccountNavbar from "./AccountNavbar";
import AppFooter from "../components/AppFooter";
import AccountSignedOutEmptyState from "./AccountSignedOutEmptyState";
import {get, handleErrorFetch, post, put, remove} from "../services/APIService";
import {failedToast, successToast} from "../helpers/ToastHelper";
import {ToastContainer} from "react-toastify";
import {
    FING_ROLE_CONTRIBUTOR,
    FING_ROLE_EDITOR,
    FING_ROLE_INSTALLER, FING_ROLE_MANAGER, FING_ROLE_OWNER,
    FING_ROLE_VIEWER, rolePrettyDescription,
    rolePrettyName
} from "../helpers/BillingHelper";
import {isPermissionAllowedForWorkspace} from "../helpers/AccountHelper";

class WorkspaceManage extends Component {

    static contextType = UserContext;

    constructor(props) {
        super(props);

        this.state = {
            showInviteModal: false,
            showEditRoleModal: false,
            showDeleteModal: false,
            invitedUserEmail: "",
            invitedUserRole: FING_ROLE_VIEWER,
            workspace: null,
            workspaceName: "",
            accesses: null,
            selectedAccess: null,
        };
    }

    componentDidMount() {
        document.title = "Fing - Manage workspace";
        const query = new URLSearchParams(this.props.location.search);
        const workspaceId = query && query.has("wid") ? query.get("wid") : null;
        get("/rest/workspace/list")
            .then((workspaces) => {
                const workspace = workspaces.find(ws => ws.workspaceId === workspaceId);
                if (!workspace) {
                    this.setState({workspace: {}})
                    return;
                }

                this.setState({workspace: workspace, workspaceName: workspace.name});
                this.fetchWorkspaceAccessList(workspace);
            })
            .catch((e) => {
                console.log('Error in get auth apps: ', e);
                this.setState({workspace: {}})
            });
    }

    fetchWorkspaceAccessList(workspace) {
        get(`/rest/workspace/access/invitations?workspaceId=${workspace.workspaceId}`)
            .then((accesses) => {
                this.setState({accesses: accesses});
            })
            .catch((e) => {
                console.log('Error in get auth apps: ', e);
                this.setState({accesses: []})
            });
    }

    validateRestResponse(response) {
        if (response.status !== 200) {
            throw Error(response.statusText);
        }
        return response;
    }

    catchRestError(response) {
    }


    render() {
        return (
            <div>
                <AccountNavbar mode='account' />

                <main id="content" role="main">
                    {this.renderMainContent()}
                </main>

                <AppFooter />
            </div>
        );
    }

    renderMainContent() {
        return (
            <UserContext.Consumer>
                {userContext => this.renderMainContentInContext(userContext)}
            </UserContext.Consumer>
        )
    }

    renderMainContentInContext(userContext) {
        if (!userContext || !userContext.userProfileLoaded) {
            return <div className="container space-2">
                <div className="row mt-3">
                    <div className="col-lg-12 text-center">
                        <WaitingComponent className="my-3" />
                    </div>
                </div>
            </div>;
        } else if (isUserSignedIn(userContext) && userContext.stripePublicApiKey) {
            return this.renderMainContentSignedIn(userContext);
        } else {
            return this.renderMainContentPromo();
        }
    }

    renderMainContentSignedIn(userContext) {
        const {workspace, accesses, workspaceName} = this.state;

        // Still loading
        if (!workspace) {
            return <div className="container space-2">
                <div className="row mt-3">
                    <div className="col-lg-12 text-center">
                        <WaitingComponent className="my-3" />
                    </div>
                </div>
            </div>;
        }

        // Loaded but not found
        if (workspace && !workspace.workspaceId) {
            return <div className="bg-light">
                <div className="container space-1">
                    <div className="row justify-content-md-between align-items-md-center mb-10">
                        <div className="col-col-md-7 col-xl-6">
                            <div className="mb-6">
                                <h1 className="display-5 mb-3">Workspace not found</h1>
                                <p className="lead mb-9">Please check your access level.</p>
                            </div>
                            <div>
                                <a className="btn btn-primary btn-wide mr-lg-4"
                                   href="/account/workspace">Back to workspaces</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>;
        }

        const onInvite = () => {
            this.setState({showInviteModal: true});
        };

        const onSave = (event) => {
            event.preventDefault();

            if (!workspaceName || workspaceName.trim().length === 0) {
                return;
            }

            const payload = {
                workspaceId: workspace.workspaceId,
                name: workspaceName.trim()
            }

            put('/rest/workspace', payload)
                .then((updatedWorkspace) => {
                    successToast("Workspace updated.");
                    this.setState({workspace: updatedWorkspace});
                })
                .catch((error) => {
                    failedToast("Something went wrong.");

                    if (error && error.code === 401) {
                        let newContext = handleErrorFetch(userContext);
                        userContext.userContextUpdater(newContext);
                    }

                    console.log('Error in manage workspace: ', error)
                })

        };

        const onDelete = () => {
            this.setState({showDeleteModal: true});
        };

        const canManageWorkspace = isPermissionAllowedForWorkspace(userContext.userProfile, workspace.workspaceId, "manage_workspace");
        return (
            <div className="bg-light">
                <div className="container space-1">

                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h3 className="mb-0">{workspace.name}</h3>
                            <p className="text-muted mb-0">Review and manage your access to shared workspaces</p>
                        </div>
                    </div>

                    <div className="row my-4">
                        <div className="col-sm-12 col-md-3 py-2">
                            <h4>Workspace info</h4>
                        </div>
                        <div className="col-sm-12 col-md-9">
                            <div className="card card-bordered">
                                <div className="card-body">

                                    <div className="row align-items-center">
                                        <div className="col-lg-4">
                                            <label>Name</label>
                                            <small className="d-block">Names will be displayed in the App.</small>
                                        </div>
                                        <div className="col-lg-8">
                                            <div className="form-group pt-2">
                                                <input className="form-control" onChange={(e) => this.setState({workspaceName: e.target.value})}
                                                       name="name" placeholder="e.g. ACME Workspace"
                                                       value={workspaceName}/>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                {
                                    <div className="card-footer d-flex">
                                        <button className="btn btn-xs btn-primary mr-3"
                                                disabled={!workspaceName || !canManageWorkspace}
                                                onClick={onSave}>Save changes</button>
                                        <button className="btn btn-xs btn-ghost-danger ml-auto"
                                                disabled={!canManageWorkspace || !workspace || !workspace.workspaceId || (workspace.networkCount+workspace.agentCount > 0) }
                                                onClick={onDelete}><i className="fa fa-trash-alt mr-1" />Delete workspace</button>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>

                    <div className="row my-4">
                        <div className="col-sm-12 col-md-3 py-2">
                            <h4>Team members</h4>
                        </div>
                        <div className="col-sm-12 col-md-9">
                            <div className="card card-bordered">
                                <table className="table table-responsive-lg">
                                    <thead>
                                        <tr>
                                            <th>User</th>
                                            <th>Role</th>
                                            <th>State</th>
                                            <th></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {accesses && accesses.map((wa,idx) => <tr key={`wa_${idx}`}>
                                            <td>{this.renderUser(wa)}</td>
                                            <td>{this.renderRole(wa)}</td>
                                            <td>{this.renderAccessState(wa)}</td>
                                            <td>{this.renderAccessActions(wa)}</td>
                                        </tr>)}
                                    </tbody>
                                </table>

                                {
                                    canManageWorkspace &&
                                    <div className="card-footer">
                                        <button className="btn btn-xs btn-primary" onClick={onInvite}>Invite user</button>
                                        <div className="small mt-2">At this time, an account must be created before sending an invitation.</div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>

                </div>

                {this.renderInviteUserModal(userContext)}
                {this.renderEditRoleModal(userContext)}
                {this.renderDeleteModal(userContext)}
                {this.renderRevokeModal(userContext)}
                {this.renderResendModal(userContext)}
                <ToastContainer />
            </div>
        );
    }

    renderMainContentPromo() {
        return <AccountSignedOutEmptyState />;
    }

    renderUser(wa) {
        return wa.userId;
    }

    renderRole(wa) {
        return rolePrettyName(wa.role);
    }

    renderAccessState(wa) {
        const {role, accessState, joinDate, invitationDate, leaveDate} = wa;
        if (accessState === "GRANTED" && role === FING_ROLE_OWNER)
            return <><span className="fas fa-fw mr-1 fa-check-circle text-green"/>{`Created on ${new Date(joinDate).toLocaleDateString()}`}</>;
        else if (accessState === "GRANTED" && joinDate)
            return <><span className="fas fa-fw mr-1 fa-check-circle text-green"/>{`Joined on ${new Date(joinDate).toLocaleDateString()}`}</>;
        else if (accessState === "PENDING" && invitationDate)
            return <><span className="fas fa-fw mr-1 fa-hourglass text-blue"/>{`Invited on ${new Date(invitationDate).toLocaleDateString()}`}</>;
        else if (accessState === "LEFT" && leaveDate)
            return <><span className="fa fa-fw mr-1 fa-times-circle text-gray"/>{`Left on ${new Date(leaveDate).toLocaleDateString()}`}</>;
        else
            return accessState || "-";
    }

    renderAccessActions(wa) {
        const {role, accessState } = wa;

        const onEdit = () => {
            this.setState({showEditRoleModal: true, selectedAccess: wa, invitedUserRole: wa.role});
        };

        const onRevoke = () => {
            this.setState({showRevokeModal: true, selectedAccess: wa});
        };

        const onResend = () => {
            this.setState({showResendModal: true, selectedAccess: wa});
        };

        if (accessState === "GRANTED" && role !== FING_ROLE_OWNER) {
            return <div className="d-flex justify-content-end flex-sm-nowrap">
                <button onClick={onEdit} className="btn btn-xs btn-outline-gray-dark ml-2"><span className="fa fa-pen"/></button>
                <button onClick={onRevoke} className="btn btn-xs btn-outline-gray-dark ml-2"><span className="fa fa-trash-alt"/></button>
                <Link to={`/account/workspace/notifications?aid=${wa.accessId}`}
                      className="btn btn-xs btn-outline-gray-dark ml-2"><span className="fa fa-bell"/></Link>
            </div>
        }

        if (accessState === "GRANTED" && role === FING_ROLE_OWNER) {
            return <div className="d-flex justify-content-end flex-sm-nowrap">
                <Link to={`/account/workspace/notifications?aid=${wa.accessId}`}
                      className="btn btn-xs btn-outline-gray-dark ml-2"><span className="fa fa-bell"/></Link>
            </div>
        }

        if (accessState === "LEFT") {
            return <div className="d-flex justify-content-end flex-sm-nowrap">
                <button onClick={onRevoke} className="btn btn-xs btn-gray-dark">Remove</button>
            </div>
        }
        if (accessState === "PENDING") {
            return <div className="d-flex justify-content-end flex-sm-nowrap">
                <button onClick={onResend} className="btn btn-xs btn-outline-primary ml-2"><span className="fa fa-envelope mr-1"/>Resend</button>
                <button onClick={onRevoke} className="btn btn-xs btn-outline-gray-dark ml-2"><span className="fa fa-trash-alt"/></button>
            </div>
        }

        return "";
    }

    // ==========================================================================================================

    renderInviteUserModal(userContext) {

        const { workspace, showInviteModal, invitedUserEmail, invitedUserRole} = this.state;

        const handleClose = () => this.setState({ showInviteModal: false, invitedUserEmail: "", invitedUserRole: FING_ROLE_VIEWER });

        const handleEmailChange = (evt) => this.setState({ invitedUserEmail: evt.target.value});
        const handleRoleChange = (evt) => this.setState({ invitedUserRole: evt.target.value});

        const handleInvite = () => {

            const { invitedUserEmail, invitedUserRole } = this.state;

            if (invitedUserEmail && invitedUserEmail.length > 0 && invitedUserRole !== null) {

                const payload = {
                    "workspaceId": workspace.workspaceId,
                    "userId": invitedUserEmail,
                    "role": invitedUserRole
                };

                return post("/rest/workspace/access/send", payload)
                    .then((newAccess) => {
                        successToast(`Invitation sent to ${invitedUserEmail} as ${rolePrettyName(invitedUserRole)}.`);
                        this.setState((prevState, props) => {
                            const accesses = prevState.accesses;
                            return {
                                accesses: [...accesses, newAccess]
                            }
                        });
                    })
                    .catch((err) => {
                        console.log('Error in invite user: ', err)

                        if (err && err.code === 401) {
                            let newContext = handleErrorFetch(userContext);
                            userContext.userContextUpdater(newContext);
                        } else if (err && err.code === 404) {
                            failedToast("User not found. A Fing account is required in order to be invited to Workspaces.")
                        } else {
                            failedToast("Something went wrong while inviting a user");
                        }

                        })
                    .finally(() => this.setState({ showInviteModal: false, invitedUserEmail: "", invitedUserRole: FING_ROLE_VIEWER }))
            }

        }

        return (
            <div>
                <div className={`modal-backdrop fade ${showInviteModal ? "show d-block" : "d-none"}`}></div>
                <div className={`modal fade ${showInviteModal ? "show d-block" : "d-none"}`} role="dialog" tabIndex="-1"
                     aria-labelledby="Delete_Modal_label" aria-hidden={!showInviteModal} aria-modal={showInviteModal} >
                    <div className="modal-dialog modal-md" role="document">
                        <div className="modal-content">
                            <div id="Delete_Modal_label" className="modal-header p-3">
                                <h4 className="modal-title">Invite a user to join this workspace</h4>
                                <button type="button" className="close" onClick={handleClose} data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true">×</span><span className="sr-only">Close</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <h6 className="">Email</h6>
                                <div className="input-group input-group-sm">
                                    <input type="email" value={invitedUserEmail} onChange={handleEmailChange}
                                           placeholder={"Email address to send the invite to"}
                                           maxLength={80}
                                           className={"form-control " + (invitedUserEmail ? "" : "is-invalid")} />
                                </div>
                                <h6 className="mt-4">Role</h6>
                                <div className="input-group input-group-sm">
                                    <select value={invitedUserRole} onChange={handleRoleChange}
                                           className={"form-control"}>
                                        {
                                            [FING_ROLE_VIEWER, FING_ROLE_EDITOR, FING_ROLE_CONTRIBUTOR, FING_ROLE_INSTALLER, FING_ROLE_MANAGER]
                                                .map(role => <option key={role} value={role}>{rolePrettyName(role)}&nbsp;&nbsp;&nbsp;({rolePrettyDescription(role)})</option>)
                                        }
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <div className="d-flex justify-content-end">
                                    <button className="btn btn-outline-secondary btn-xs mr-2"
                                            onClick={handleClose}>Cancel
                                    </button>
                                    <button className="btn btn-primary btn-xs"
                                            onClick={handleInvite} disabled={!invitedUserEmail || invitedUserEmail.length === 0} >Send invite
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );

    }

    renderEditRoleModal(userContext) {
        const { showEditRoleModal, selectedAccess, invitedUserRole} = this.state;
        if (!selectedAccess || !selectedAccess.userId) return '';

        const handleClose = () => this.setState({ showEditRoleModal: false, selectedAccess: null, invitedUserRole: FING_ROLE_VIEWER });

        const handleRoleChange = (evt) => this.setState({ invitedUserRole: evt.target.value});

        const handleApply = () => {
            if (selectedAccess !== null && invitedUserRole !== null) {

                const payload = {
                    "accessId": selectedAccess.accessId,
                    "role": invitedUserRole
                };

                return put("/rest/workspace/access/change", payload)
                    .then((newAccess) => {
                        successToast(`Role changed for ${selectedAccess.userId} as ${rolePrettyName(invitedUserRole)}.`);
                        this.setState((prevState, props) => {
                            return {
                                accesses: prevState.accesses.map(a => a.accessId === newAccess.accessId ? newAccess : a)
                            }
                        });
                    })
                    .catch((err) => {
                        console.log('Error in invite user: ', err)

                        if (err && err.code === 401) {
                            let newContext = handleErrorFetch(userContext);
                            userContext.userContextUpdater(newContext);
                        } else if (err && err.code === 404) {
                            failedToast("User not found")
                        } else {
                            failedToast("Something went wrong while editing a user's role");
                        }

                    })
                    .finally(() => this.setState({ showEditRoleModal: false, selectedAccess: null, invitedUserRole: FING_ROLE_VIEWER }))
            }

        }


        return (
            <div>
                <div className={`modal-backdrop fade ${showEditRoleModal ? "show d-block" : "d-none"}`}></div>
                <div className={`modal fade ${showEditRoleModal ? "show d-block" : "d-none"}`} role="dialog" tabIndex="-1"
                     aria-labelledby="Delete_Modal_label" aria-hidden={!showEditRoleModal} aria-modal={showEditRoleModal} >
                    <div className="modal-dialog modal-md" role="document">
                        <div className="modal-content">
                            <div id="Delete_Modal_label" className="modal-header p-3">
                                <h4 className="modal-title">Change user role in this workspace</h4>
                                <button type="button" className="close" onClick={handleClose} data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true">×</span><span className="sr-only">Close</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <h6>Role</h6>
                                <div className="input-group input-group-sm">
                                    <select value={invitedUserRole} onChange={handleRoleChange}
                                            className={"form-control"}>
                                        {
                                            [FING_ROLE_VIEWER, FING_ROLE_EDITOR, FING_ROLE_CONTRIBUTOR, FING_ROLE_INSTALLER, FING_ROLE_MANAGER]
                                                .map(role => <option key={role} value={role}>{rolePrettyName(role)}</option>)
                                        }
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <div className="d-flex justify-content-end">
                                    <button className="btn btn-outline-secondary btn-xs mr-2"
                                            onClick={handleClose}>Cancel
                                    </button>
                                    <button className="btn btn-primary btn-xs"
                                            onClick={handleApply}>Apply
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );

    }

    renderDeleteModal(userContext) {
        const { showDeleteModal, workspace } = this.state;

        const handleClose = () => this.setState({ showDeleteModal: false });

        const handleSubmit = () => {
            const url = `/rest/workspace/${workspace.workspaceId}`;

            return remove(url)
                .then(() => {
                    successToast("Workspace deleted.");
                    window.location.href = `${process.env.PUBLIC_URL}/account/workspace`;
                })
                .catch((err) => {
                    failedToast("Something went wrong.");
                    if (err && err.code === 401) {
                        let newContext = handleErrorFetch(userContext);
                        userContext.userContextUpdater(newContext);
                    }
                    console.log('Error in deleting workspace: ', err)
                })
                .finally(() => this.setState({ showDeleteModal: false }))

        }

        return this.renderModal(showDeleteModal, true, handleClose, handleSubmit,
            `Delete ${workspace.name}?`,
            <p>The Workspace '<span className="text-primary">{workspace.name}</span>' will be removed and all its content lost. Do you want to proceed?</p>,
            "Delete");
    }

    renderRevokeModal(userContext) {
        const { showRevokeModal, workspace, selectedAccess } = this.state;
        if (!workspace || !workspace.workspaceId || !selectedAccess || !selectedAccess.userId) return '';

        const handleClose = () => this.setState({ showRevokeModal: false });

        const handleSubmit = () => {
            const payload = {
                accessId: selectedAccess.accessId,
                userId: selectedAccess.userId
            }

            return remove('/rest/workspace/access/revoke', payload)
                .then(() => {
                    successToast("Access revoked.");
                    this.fetchWorkspaceAccessList(workspace);
                })
                .catch((err) => {
                    failedToast("Something went wrong.");
                    if (err && err.code === 401) {
                        let newContext = handleErrorFetch(userContext);
                        userContext.userContextUpdater(newContext);
                    }
                    console.log('Error in revoking access: ', err)
                })
                .finally(() => this.setState({ showRevokeModal: false }))

        }

        return this.renderModal(showRevokeModal, true, handleClose, handleSubmit,
            `Revoke access to ${workspace.name}?`,
            <p>The access to Workspace '<span className="text-primary">{workspace.name}</span>' will be revoked to the user
                '<span className="text-primary">{selectedAccess.userId}</span>'. Do you want to proceed?</p>,
            "Revoke");
    }

    renderResendModal(userContext) {
        const { showResendModal, workspace, selectedAccess } = this.state;
        if (!workspace || !workspace.workspaceId || !selectedAccess || !selectedAccess.userId) return '';

        const handleClose = () => this.setState({ showResendModal: false });

        const handleSubmit = () => {
            const url = "/rest/workspace/access/refresh";
            const payload = {
                accessId: selectedAccess.accessId,
                userId: selectedAccess.userId
            }

            return put(url, payload)
                .then(() => {
                    successToast("Access invitation resent.");
                    this.fetchWorkspaceAccessList(workspace);
                })
                .catch((err) => {
                    failedToast("Something went wrong.");
                    if (err && err.code === 401) {
                        let newContext = handleErrorFetch(userContext);
                        userContext.userContextUpdater(newContext);
                    }
                    console.log('Error in revoking access: ', err)
                })
                .finally(() => this.setState({ showResendModal: false }))

        }

        return this.renderModal(showResendModal, false, handleClose, handleSubmit,
            `Resend access to ${workspace.name}?`,
            <p>The invitation to access to Workspace '<span className="text-primary">{workspace.name}</span>' will be sent again to the user
                '<span className="text-primary">{selectedAccess.userId}</span>'. Do you want to proceed?</p>,
            "Resend");
    }

    renderModal(showModal, destructive, handleClose, handleSubmit, title, body, submitLabel) {
        return <div>
            <div className={`modal-backdrop fade ${showModal ? "show d-block" : "d-none"}`}></div>
            <div className={`modal fade ${showModal ? "show d-block" : "d-none"}`} role="dialog" tabIndex="-1"
                 aria-labelledby="Modal_label" aria-hidden={!showModal} aria-modal={showModal} >
                <div className="modal-dialog modal-md" role="document">
                    <div className="modal-content">
                        <div className={"modal-header p-3" + (destructive && " bg-danger")}>
                            <h4 id="Modal_label" className={"modal-title" + (destructive && " text-white")}>{title}</h4>
                            <button type="button" className="close text-white" onClick={handleClose} data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">×</span><span className="sr-only">Close</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="container">
                                {body}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="d-flex justify-content-end">
                                <button className={"btn btn-xs mr-2 " + (destructive ? "btn-danger" : "btn-primary")}
                                        onClick={handleSubmit}>{submitLabel}
                                </button>
                                <button className="btn btn-outline-secondary btn-xs"
                                        onClick={handleClose}>Dismiss
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>;

    }
}

export default withRouter(WorkspaceManage)